"use client";

import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  getAuth,
  type Auth,
  GoogleAuthProvider,
  type GoogleAuthProvider as GoogleAuthProviderType,
  GithubAuthProvider,
  type GithubAuthProvider as GithubAuthProviderType,
  User,
} from "firebase/auth";
import { getStorage, type FirebaseStorage } from "firebase/storage";
import { installIdentityToolkitProxy } from "@/lib/firebase/installIdentityToolkitProxy";

const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
};

// Check if Firebase config is valid
const isFirebaseConfigValid = () => {
  return (
    firebaseConfig.apiKey &&
    firebaseConfig.authDomain &&
    firebaseConfig.projectId &&
    firebaseConfig.storageBucket &&
    firebaseConfig.messagingSenderId &&
    firebaseConfig.appId
  );
};

// Initialize Firebase only if config is valid
let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let storage: FirebaseStorage | null = null;
let googleProvider: GoogleAuthProviderType | null = null;
let githubProvider: GithubAuthProviderType | null = null;

if (isFirebaseConfigValid()) {
  try {
    installIdentityToolkitProxy();
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    storage = getStorage(app);
    googleProvider = new GoogleAuthProvider();
    githubProvider = new GithubAuthProvider();
  } catch (error) {
    console.warn("Firebase initialization failed:", error);
  }
} else {
  console.warn(
    "Firebase config is incomplete. Firebase will not be initialized."
  );
}

// Export with fallback values
export { auth, storage, googleProvider, githubProvider };

// Alias exports for backward compatibility
export const firebaseAuth = auth;
export const firebaseStorage = storage;

// Helper function to check if Firebase is initialized
export const isFirebaseInitialized = () => {
  return app !== null && auth !== null;
};

// Function to check if user has admin privileges
export async function checkIsAdmin(user: User | null): Promise<boolean> {
  if (!user) {
    return false;
  }

  try {
    const tokenResult = await user.getIdTokenResult();
    return tokenResult.claims.admin === true;
  } catch (error) {
    console.error("Error checking admin status:", error);
    return false;
  }
}
